import {
  Component,
  DestroyRef,
  HostBinding,
  inject,
  OnInit,
  QueryList,
  Renderer2,
  signal,
  Signal,
  Type,
  ViewChildren,
  ViewContainerRef,
  WritableSignal,
} from '@angular/core';
import { CmsService, RoutingService, WindowRef } from '@boels/helpers';

import { CommonModule } from '@angular/common';
import { combineLatestWith, filter, map, Observable, of, take } from 'rxjs';
import {
  CmsComponent,
  ComponentConfig,
  ContentSlot,
  Page,
  SupportedTypeCodes,
} from '@boels/models';
import { componentsConfig } from '../components.config';
import {
  CartFacade,
  SlotDecoratorDirective,
  SmartEditService,
  triggerLoad$,
} from '@boels/shared-state';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { VerticalAlignerComponent } from '../../vertical-aligner/vertical-aligner.component';
import { EmptyComponent } from '../../page-not-found/page-not-found.component';

@Component({
  selector: 'boels-content-template-renderer',
  imports: [
    CommonModule,
    SlotDecoratorDirective,
    VerticalAlignerComponent,
    EmptyComponent,
  ],
  templateUrl: './content-template-renderer.component.html',
  styleUrls: ['./content-template-renderer.component.scss'],
})
export class ContentTemplateRendererComponent implements OnInit {
  #cmsService = inject(CmsService);
  #cartFacade = inject(CartFacade);
  #renderer = inject(Renderer2);
  #smartEditService = inject(SmartEditService);
  #routingService = inject(RoutingService);
  #winRef = inject(WindowRef);
  #destroyRef = inject(DestroyRef);

  @HostBinding('class') pageTemplate: string | undefined;

  @ViewChildren('slotsRenderer', { read: ViewContainerRef })
  slotsRenderer: QueryList<ContentTemplateRendererComponent>;

  @ViewChildren('topSlotsRenderer', { read: ViewContainerRef })
  topSlotsRenderer: QueryList<ContentTemplateRendererComponent>;

  @ViewChildren('bottomSlotsRenderer', { read: ViewContainerRef })
  bottomSlotsRenderer: QueryList<ContentTemplateRendererComponent>;

  $page: Signal<Page | undefined> = signal(
    this.#routingService.getRoutePropertyWithValue<Page, 'data'>(
      'data',
      'routeResolver',
    ),
  );
  $cartState: Signal<{ loading: boolean; count: number | undefined }> =
    toSignal(this.getCartState(), {
      initialValue: {
        loading: true,
        count: undefined,
      },
    });
  $pageSlots: WritableSignal<ContentSlot[] | undefined> = signal(undefined);
  $topSlots: WritableSignal<ContentSlot[] | undefined> = signal(undefined);
  $bottomSlots: WritableSignal<ContentSlot[] | undefined> = signal(undefined);
  $pageFold: WritableSignal<ContentSlot | undefined> = signal(undefined);

  ngOnInit(): void {
    triggerLoad$
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.#winRef.document.location.reload();
      });

    const page = { ...this.$page() };
    this.loadPageData(page);
  }

  loadPageData(page: Page) {
    const pageSlots = this.#cmsService.getMainLayoutSlots(page);
    const topSlots = this.#cmsService.getTopLayoutSlots(page);
    const bottomSlots = this.#cmsService.getBottomLayoutSlots(page);
    const pageFold = this.#cmsService.getFoldBreakpointSlot(page);

    this.$pageSlots.set(pageSlots);
    this.$topSlots.set(topSlots);
    this.$bottomSlots.set(bottomSlots);
    this.$pageFold.set(pageFold);
    this.pageTemplate = page.template;

    this.createComponents();
  }

  getCartState(): Observable<{ loading: boolean; count: number | undefined }> {
    return this.#cartFacade.getActiveCart(true).pipe(
      combineLatestWith(this.#cartFacade.getCartLoading()),
      filter((latest) => !latest[1]),
      map((latest) => ({
        loading: false,
        count: latest[0]?.totalUnitCount,
      })),
    );
  }

  createComponents(): void {
    this.createTopComponents();
    this.createMainComponents();
    this.createBottomComponents();
  }

  createMainComponents(): void {
    let components = [];
    this.$pageSlots()?.forEach(
      async (slot: ContentSlot | any, index: number) => {
        components = await this.fetchResolvedComponents(slot);
        this.renderComponents(components, index);
      },
    );
  }

  createTopComponents(): void {
    let components = [];
    this.$topSlots()?.forEach(
      async (slot: ContentSlot | any, index: number) => {
        components = await this.fetchResolvedComponents(slot);
        this.renderComponents(components, index, 'top');
      },
    );
  }

  createBottomComponents(): void {
    let components = [];
    this.$bottomSlots()?.forEach(
      async (slot: ContentSlot | any, index: number) => {
        components = await this.fetchResolvedComponents(slot);
        this.renderComponents(components, index, 'bottom');
      },
    );
  }

  fetchResolvedComponents(
    slot: ContentSlot | any,
  ): Promise<{ component: Type<any>; cmsComponent: CmsComponent }[]> {
    return Promise.all(
      slot.components.component.map(async (cmsComponent: CmsComponent) => {
        const component = await this.getComponent(cmsComponent);
        return { component, cmsComponent };
      }),
    );
  }

  renderComponents(
    components: any[],
    slotIndex: number,
    position?: string,
  ): void {
    components
      ?.filter((component: any) => component.component)
      .forEach((component: any) => {
        const mappedComponentInputs: any = this.getComponentDefaultInputs(
          component.cmsComponent,
        );
        this.renderDataComponent(
          component.cmsComponent,
          slotIndex,
          component.component,
          mappedComponentInputs,
          position,
        );
      });
  }

  async getComponent(cmsComponent: CmsComponent): Promise<Type<any>> {
    return this.getComponentType(
      cmsComponent.typeCode &&
        Object.keys(SupportedTypeCodes).includes(cmsComponent.typeCode)
        ? cmsComponent.flexType
        : cmsComponent.typeCode,
    )?.component();
  }

  getComponentDefaultInputs(cmsComponent: CmsComponent): {
    key: string;
    value: any;
  } | null {
    return (
      this.getComponentType(
        cmsComponent.typeCode &&
          Object.keys(SupportedTypeCodes).includes(cmsComponent.typeCode)
          ? cmsComponent.flexType
          : cmsComponent.typeCode,
      )?.inputs || null
    );
  }

  createComponent(
    slotIndex: number,
    mappedComponent?: Awaited<Promise<any>>,
  ): any {
    return this.slotsRenderer.get(slotIndex)?.createComponent(mappedComponent);
  }

  createTopComponent(
    slotIndex: number,
    mappedComponent?: Awaited<Promise<any>>,
  ): any {
    return this.topSlotsRenderer
      .get(slotIndex)
      ?.createComponent(mappedComponent);
  }

  createBottomComponent(
    slotIndex: number,
    mappedComponent?: Awaited<Promise<any>>,
  ): any {
    return this.bottomSlotsRenderer
      .get(slotIndex)
      ?.createComponent(mappedComponent);
  }

  renderDataComponent(
    cmsComponent: CmsComponent,
    slotIndex: number,
    mappedComponent: Awaited<Promise<any>>,
    mappedComponentInputs?: any,
    position?: string,
  ): void {
    let renderedComponent: any;
    if (position === 'top')
      renderedComponent = this.createTopComponent(slotIndex, mappedComponent);
    else if (position === 'bottom')
      renderedComponent = this.createBottomComponent(
        slotIndex,
        mappedComponent,
      );
    else renderedComponent = this.createComponent(slotIndex, mappedComponent);

    if (renderedComponent)
      renderedComponent.instance.componentData = {
        data$: of(cmsComponent),
      };

    if (renderedComponent && cmsComponent.properties) {
      this.#smartEditService.addSmartEditContract(
        renderedComponent.location.nativeElement,
        this.#renderer,
        cmsComponent.properties,
      );
    }

    if (mappedComponentInputs)
      Object.entries(mappedComponentInputs).map(([input, value]) => {
        renderedComponent.setInput(input, value);
      });
  }

  getComponentType(typeCode?: string): ComponentConfig | undefined {
    return componentsConfig.find(
      (item: ComponentConfig) => item.cmsComponentTypeCode === typeCode,
    );
  }
}
